import type { ReactNode } from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CheckSettings,
  CostLineIndexEntry,
  DictionaryValidationMatrix,
  EngineValidationCostPair,
} from "@shared-core/data/cost-engine-validation-types";
import {
  costCheckStateLabel,
  costCheckStateTone,
  costHeadLabel,
  formatCheckRaisedBy,
  formatCostPairBannerSentence,
  formatRouteSourceLine,
  fixedCostOptionsFromSearchValues,
  fixedCostOptionsLabel,
  groupCostPairLinesByCostCentre,
  groupCostPairLinesByCostHead,
  quantitiesInVariant,
  variantsAtQuantity,
} from "@shared-core/domain/cost-engine-validation-presentation";
import {
  IN_HOUSE_ROUTE_CODE,
  type SupplierRouteCostPair,
} from "@shared-core/data/fulfilment-route-types";
import { OURS_THARSTERN_LEGEND } from "@shared-core/domain/engine-tharstern-comparison";
import {
  ATTEMPT_OUTCOME,
  DELIVERY_INCLUDED_NOTE,
  SUPPLIER_COST_FRESHNESS,
  formatDifferenceLabel,
  formatRequestAttributeSource,
} from "@shared-core/domain/fulfilment-route-presentation";
import {
  IN_HOUSE_ROW_LABEL,
  describeCheckVerdict,
  describeInHouseCell,
  inHouseFigurePence,
} from "@shared-core/domain/cost-matrix-cells";
import {
  formatCurrencyGbp,
  formatDateTimeOrDash,
  formatLongWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  ComparisonCells,
  ComparisonGroupHeaderCell,
  ComparisonInline,
  ComparisonSubHeaderCells,
} from "../app/components/patterns/ComparisonTriplet";
import { ManufacturingPressesConsideredTable } from "../app/components/domain/manufacturing/ManufacturingPressesConsideredTable";
import { MarkMessage } from "../app/components/domain/catalogue/MarkMessage";
import { ChevronDown, ChevronRight } from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

/**
 * The dictionary cost-pair page: state banner, then Calculation (lines
 * grouped by cost head, then by cost centre — each cost centre carries its
 * own one-line "why" and, for a press, an Alternatives expander),
 * Specification as costed, and Check history. Fetches everything itself
 * from the three route params — a direct load never depends on having come
 * from the matrix page.
 */
/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Dictionary › {item}, plus this pair's own variant/quantity
 *  once it has loaded. */
function crumbs(
  dictionaryItemId: string | undefined,
  itemName?: string,
  leaf?: string,
) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Dictionary", to: paths.dictionaryAdmin() },
  ];
  if (!itemName || !dictionaryItemId) return base;
  return leaf
    ? [
        ...base,
        {
          label: itemName,
          to: paths.dictionaryItemAdmin({ dictionaryItemId }),
        },
        { label: leaf },
      ]
    : [...base, { label: itemName }];
}

/** One cost on one fulfilment route. `?route=<supplier>` shows that
 *  supplier's cost for the same (variant, quantity) — what we sent, what
 *  came back and when (catalogue third-party fulfilment brief §8, journey
 *  A); without it, the in-house cost pair exactly as before. */
export function DictionaryCostPairPage() {
  const [searchParams] = useSearchParams();
  const routeCode = searchParams.get("route");
  return routeCode && routeCode !== IN_HOUSE_ROUTE_CODE ? (
    <SupplierRouteCostPairView routeCode={routeCode} />
  ) : (
    <InHouseCostPair />
  );
}

function InHouseCostPair() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const {
    dictionaryItemId,
    variantId,
    quantity: quantityParam,
  } = useParams<{
    dictionaryItemId: string;
    variantId: string;
    quantity: string;
  }>();
  const quantity = quantityParam ? Number(quantityParam) : NaN;
  // A fixed-cost corner (spec 03 rule (d)) is this variant with some
  // fixed-cost options on — its own price, named in the URL's search.
  const [searchParams] = useSearchParams();
  const searchOptionsKey = searchParams.getAll("fixedCostOption").join("|");
  const fixedCostOptions = useMemo(
    () =>
      fixedCostOptionsFromSearchValues(
        searchOptionsKey ? searchOptionsKey.split("|") : [],
      ),
    [searchOptionsKey],
  );

  const [pair, setPair] = useState<EngineValidationCostPair | null>(null);
  const [matrix, setMatrix] = useState<DictionaryValidationMatrix | null>(null);
  // The driving cost line's own aggregate figures (checkCount/missCount/
  // moneySummary) — the banner sentence's "differs on 9 of 30 checks" reuses
  // the same numbers the by-cost-line board shows, never a re-derivation
  // from the (possibly paged/capped) check-history listing. Fetched
  // alongside pair/matrix and committed in the same render pass (not a
  // second, later `setState`) so the banner never flashes "Not yet checked"
  // while the real reason is still in flight.
  const [costLine, setCostLine] = useState<CostLineIndexEntry | null>(null);
  // The check-settings window (weeks) backs the "Matches Tharstern on all N
  // checks in the last M weeks" banner for a green pair with no specific
  // driving cost line — fetched alongside pair/matrix/costLine, never a
  // second later `setState` (coordinator review round 5).
  const [checkSettings, setCheckSettings] = useState<CheckSettings | null>(
    null,
  );
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (!dictionaryItemId || !variantId || Number.isNaN(quantity)) return;
    setLoadError(false);
    Promise.all([
      adapter.getEngineValidationCostPair(
        dictionaryItemId,
        variantId,
        quantity,
        fixedCostOptions,
      ),
      // No filter/paging options — adjacency below must see every row.
      adapter.getDictionaryValidationMatrix(dictionaryItemId),
      adapter.getCheckSettings(),
    ])
      .then(async ([p, m, settings]) => {
        if (p === null || m === null) {
          setNotFound(true);
          return;
        }
        const line = p.costLine
          ? await adapter.getValidationCostLine(
              p.costLine.costCentreId,
              p.costLine.component,
            )
          : null;
        setPair(p);
        setMatrix(m);
        setCostLine(line);
        setCheckSettings(settings);
      })
      .catch(() => setLoadError(true));
  }, [adapter, dictionaryItemId, variantId, quantity, fixedCostOptions]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-destructive">
          Failed to load this cost pair&rsquo;s data.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!dictionaryItemId || !variantId || Number.isNaN(quantity)) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">
          This cost pair link is malformed.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (notFound) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">
          No such dictionary item, or no such variant/quantity combination on
          it. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!pair || !matrix || !checkSettings) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">Loading…</p>
      </BreadcrumbsOnly>
    );
  }

  // The pair response carries the options' catalogue labels; the URL only
  // their codes.
  const pairOptions = pair.fixedCostOptions ?? [];
  const optionsLabel = fixedCostOptionsLabel(pairOptions);
  const quantityOptions = quantitiesInVariant(matrix, variantId, pairOptions);
  const variantOptions = variantsAtQuantity(matrix, quantity);

  // Another quantity of the same corner keeps its options; choosing another
  // variant goes to that variant as generated.
  const goToQuantity = (nextQuantity: number) =>
    navigate(
      paths.dictionaryCostPair({
        dictionaryItemId,
        variantId,
        quantity: nextQuantity,
        fixedCostOptions: pairOptions,
      }),
    );
  const goToVariant = (nextVariantId: string) =>
    navigate(
      paths.dictionaryCostPair({
        dictionaryItemId,
        variantId: nextVariantId,
        quantity,
      }),
    );

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={crumbs(
          dictionaryItemId,
          pair.dictionaryItemName,
          `${pair.variantLabel}${optionsLabel ? ` with ${optionsLabel}` : ""} × ${quantity.toLocaleString()}`,
        )}
      />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {pair.variantLabel}
        </h1>
        {pairOptions.length > 0 && (
          <p
            className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
            data-testid="pair-fixed-cost-options"
          >
            <span>With</span>
            {pairOptions.map((option) => (
              <Badge key={option.fieldKey} variant="outline">
                {option.label}
              </Badge>
            ))}
            <span>switched on — checked as its own price.</span>
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          {pair.dictionaryItemName}
        </p>
        <p className="text-xs text-muted-foreground">{OURS_THARSTERN_LEGEND}</p>
      </div>

      {/* Summary strip: cost-check badge, the Ours/Tharstern/Δ total (large),
          and quantity/variant pickers — the page's single most important
          line (review decision, 02_glossary_and_live_costing.md). Dropdowns
          rather than prev/next chevrons (build decision, 12 Sep) so jumping
          to a specific quantity or variant doesn't mean stepping through
          every one in between. */}
      <div className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-3">
            {/* The cell's own verdict, word for word (Ben, 24 Sep). */}
            <MarkMessage
              {...describeCheckVerdict({
                state: pair.state,
                ourPence: pair.ourTotalPence,
                tharsternPence: pair.tharsternTotalPence,
                costLine: pair.costLine,
                // Served exactly when the check substituted (spec 03 §9.4).
                isSubstitution: pair.substitution != null,
                substitution: pair.substitution,
              })}
              className="text-sm font-medium"
            />
            <ComparisonInline
              value={{
                kind: "money",
                ours: pair.ourTotalPence,
                tharstern: pair.tharsternTotalPence,
              }}
              className="text-lg font-semibold"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-muted-foreground">
                Quantity
              </span>
              <Select
                value={String(quantity)}
                onValueChange={(v) => goToQuantity(Number(v))}
              >
                <SelectTrigger aria-label="Quantity" className="h-7 w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {quantityOptions.map((q) => (
                    <SelectItem key={q} value={String(q)}>
                      {q.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-muted-foreground">Variant</span>
              <Select value={variantId} onValueChange={goToVariant}>
                <SelectTrigger aria-label="Variant" className="h-7 w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {variantOptions.map((v) => (
                    <SelectItem key={v.variantId} value={v.variantId}>
                      {v.displayLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatCostPairBannerSentence({
            cellState: pair.state,
            costLineLabel:
              pair.costLine && costLine ? pair.costLine.label : null,
            differingCheckCount: costLine?.missCount ?? 0,
            totalCheckCount: costLine?.checkCount ?? 0,
            largestOverCount: costLine?.moneySummary.largestOverCount ?? 0,
            largestUnderCount: costLine?.moneySummary.largestUnderCount ?? 0,
            pairCheckCount: pair.checkHistory.length,
            checkWindowWeeks: checkSettings.checkWindowWeeks,
          })}
        </p>
      </div>

      <CostPairLines pair={pair} />
      <SpecificationSection pair={pair} />
      <CheckHistorySection pair={pair} />
    </div>
  );
}

// ── Calculation: lines grouped by cost head, then by cost centre ────────
// The standards row moves into an expandable per-line detail (chevron)
// rather than its own column, so the table isn't eight columns wide (review
// decision, 02_glossary_and_live_costing.md). Δ on the Value comparison is
// visually dominant; Time is kept but secondary. The "why" is per cost
// centre, not per cost head (coordinator review round 2): two cost centres
// under one head — a binder and a guillotine, both "finishing" — can have
// different reasons, so each cost centre gets its own sub-header row, and a
// press cost centre's sub-header also carries its own "Alternatives"
// expander (once per press, not once per op line) — the presses-considered
// table this pair's route enumeration produced, replacing the old
// standalone "Presses considered" section and the separate "Route
// provenance" sentence entirely.

function CostPairLines({ pair }: { pair: EngineValidationCostPair }) {
  const [expandedAlternatives, setExpandedAlternatives] = useState<Set<number>>(
    new Set(),
  );

  if (pair.lines.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        Not costed yet
      </p>
    );
  }

  const toggleAlternatives = (costCentreId: number) =>
    setExpandedAlternatives((prev) => toggleInSet(prev, costCentreId));

  const groups = groupCostPairLinesByCostHead(pair.lines);

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead rowSpan={2} className="align-bottom">
              Op code
            </TableHead>
            <TableHead rowSpan={2} className="align-bottom">
              Quantity driver
            </TableHead>
            <ComparisonGroupHeaderCell
              label="Time"
              className="text-muted-foreground/70"
            />
            <ComparisonGroupHeaderCell label="Value" />
            <TableHead rowSpan={2} className="align-bottom">
              Standards
            </TableHead>
          </TableRow>
          <TableRow>
            <ComparisonSubHeaderCells />
            <ComparisonSubHeaderCells />
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const subtotalOurs = group.lines.reduce(
              (sum, l) => sum + l.ourValuePence,
              0,
            );
            const subtotalTharstern = group.lines.reduce(
              (sum, l) => sum + l.tharsternValuePence,
              0,
            );
            const costCentreGroups = groupCostPairLinesByCostCentre(
              group.lines,
            );
            return (
              <Fragment key={group.head}>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell
                    colSpan={9}
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {costHeadLabel(group.head)}
                  </TableCell>
                </TableRow>
                {costCentreGroups.map((ccGroup) => {
                  // Paper, other material and order expense carry no cost
                  // centre — no routing reason, no alternatives, no cost
                  // centre page, labelled the way the by-cost-line board
                  // labels them.
                  const costCentreId = ccGroup.costCentreId;
                  const reason =
                    costCentreId === null
                      ? undefined
                      : pair.costCentreReasons[costCentreId];
                  const isAlternativesExpanded =
                    costCentreId !== null &&
                    expandedAlternatives.has(costCentreId);
                  const firstLine = ccGroup.lines[0];
                  return (
                    <Fragment key={costCentreId ?? "no-cost-centre"}>
                      <TableRow className="bg-muted/10 hover:bg-muted/10">
                        <TableCell colSpan={9} className="text-xs font-medium">
                          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                            <span>
                              {costCentreId === null
                                ? "No cost centre"
                                : `${costCentreId} ${firstLine.costCentreCode ?? ""}`}
                            </span>
                            <span className="flex flex-wrap items-center gap-x-2 font-normal text-muted-foreground">
                              {reason && (
                                <span>
                                  {reason.text} ·{" "}
                                  <Link
                                    to={paths.settingsLiveCostingRoutingPolicy()}
                                    className="text-[color:var(--color-primary)] hover:underline"
                                  >
                                    policy v{reason.policyVersion}
                                  </Link>
                                </span>
                              )}
                              {group.head === "printing" &&
                                costCentreId !== null &&
                                reason?.partSpecLabel &&
                                pair.pressesConsidered.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleAlternatives(costCentreId)
                                    }
                                    aria-expanded={isAlternativesExpanded}
                                    className="inline-flex items-center gap-1 text-[color:var(--color-primary)] hover:underline"
                                  >
                                    {isAlternativesExpanded ? (
                                      <ChevronDown className="size-3" />
                                    ) : (
                                      <ChevronRight className="size-3" />
                                    )}
                                    Alternatives
                                  </button>
                                )}
                            </span>
                          </div>
                          {group.head === "printing" &&
                            isAlternativesExpanded &&
                            reason?.partSpecLabel && (
                              <div className="mt-2 normal-case">
                                <ManufacturingPressesConsideredTable
                                  exampleSpec={reason.partSpecLabel}
                                  presses={pair.pressesConsidered}
                                />
                              </div>
                            )}
                        </TableCell>
                      </TableRow>
                      {ccGroup.lines.map((line) => {
                        const index = pair.lines.indexOf(line);
                        const driverDiffers =
                          line.tharsternQuantityDriver != null &&
                          line.tharsternQuantityDriver !== line.quantityDriver;
                        return (
                          <TableRow key={index}>
                            <TableCell className="pl-4 font-medium">
                              {line.opCode}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {driverDiffers ? (
                                <div className="space-y-0.5">
                                  <div>Ours: {line.quantityDriver ?? "—"}</div>
                                  <div>
                                    Tharstern: {line.tharsternQuantityDriver}
                                  </div>
                                </div>
                              ) : (
                                (line.quantityDriver ?? "—")
                              )}
                            </TableCell>
                            <ComparisonCells
                              value={{
                                kind: "time",
                                ours: line.ourTimeMinutes,
                                tharstern: line.tharsternTimeMinutes,
                              }}
                              className="text-xs text-muted-foreground"
                            />
                            <ComparisonCells
                              value={{
                                kind: "money",
                                ours: line.ourValuePence,
                                tharstern: line.tharsternValuePence,
                              }}
                              deltaClassName="text-base font-bold"
                            />
                            <TableCell className="max-w-40 text-xs">
                              {line.costCentreId === null ? (
                                <span
                                  title={line.standardsRow}
                                  className="block truncate text-muted-foreground"
                                >
                                  {line.standardsRow || "—"}
                                </span>
                              ) : (
                                <Link
                                  to={paths.settingsLiveCostingCostCentreDetail(
                                    {
                                      costCentreId: line.costCentreId,
                                    },
                                  )}
                                  title={line.standardsRow}
                                  className="block truncate text-[color:var(--color-primary)] hover:underline"
                                >
                                  {line.standardsRow}
                                </Link>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  );
                })}
                <TableRow className="font-medium">
                  <TableCell
                    colSpan={5}
                    className="text-xs text-muted-foreground"
                  >
                    Subtotal — {costHeadLabel(group.head)}
                  </TableCell>
                  <ComparisonCells
                    value={{
                      kind: "money",
                      ours: subtotalOurs,
                      tharstern: subtotalTharstern,
                    }}
                    deltaClassName="font-bold"
                  />
                  <TableCell />
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      <div className="flex items-baseline justify-between gap-3 border-t border-border px-3 py-2 text-sm">
        <span className="font-medium">Total</span>
        <ComparisonInline
          value={{
            kind: "money",
            ours: pair.ourTotalPence,
            tharstern: pair.tharsternTotalPence,
          }}
          className="font-mono"
        />
      </div>
    </div>
  );
}

function toggleInSet(set: Set<number>, index: number): Set<number> {
  const next = new Set(set);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return next;
}

// ── Specification as costed ──────────────────────────────────────────────
// The spec itself — product type, finished size, quantity, then per-part
// stock/colours/pages, then finishing — never a repeat of the Calculation
// table's own quantity drivers. Differences highlighted.

/** Collapses to one line when nothing differs — a print manager scanning
 *  several pairs a day shouldn't have to re-read an eight-row table just to
 *  confirm the obvious (build decision, 12 Sep). Any difference opens the
 *  table by default, with a count so the scale of the drift is visible
 *  before reading a single row. */
function SpecificationSection({ pair }: { pair: EngineValidationCostPair }) {
  const [showTable, setShowTable] = useState(false);
  const specification = pair.specification;

  if (specification === null || specification.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Specification as costed
        </p>
        <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          Not costed yet
        </p>
      </div>
    );
  }

  const differingCount = specification.filter((f) => f.differs).length;
  const exactMatch = differingCount === 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Specification as costed
        </p>
        {!exactMatch && (
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            {differingCount} of {specification.length} fields differ
          </p>
        )}
      </div>

      {exactMatch ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          <span>Specification costed exactly as submitted</span>
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className="font-medium text-[color:var(--color-primary)] hover:underline"
          >
            {showTable ? "Hide" : "Show"}
          </button>
        </div>
      ) : null}

      {(!exactMatch || showTable) && (
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>We submitted</TableHead>
                <TableHead>Tharstern costed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specification.map((field) => (
                <TableRow
                  key={field.label}
                  className={
                    field.differs
                      ? "bg-amber-500/10 dark:bg-amber-400/10"
                      : undefined
                  }
                >
                  <TableCell className="font-medium">{field.label}</TableCell>
                  <TableCell
                    className={
                      field.differs
                        ? "font-medium text-amber-700 dark:text-amber-400"
                        : "text-muted-foreground"
                    }
                  >
                    {field.weSubmitted}
                  </TableCell>
                  <TableCell
                    className={
                      field.differs
                        ? "font-medium text-amber-700 dark:text-amber-400"
                        : "text-muted-foreground"
                    }
                  >
                    {field.tharsternCosted}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function CheckHistorySection({ pair }: { pair: EngineValidationCostPair }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Check history
      </p>
      {pair.checkHistory.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-3 text-center text-sm text-muted-foreground">
          No checks recorded for this pair yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Feed</TableHead>
              <TableHead>Checked</TableHead>
              <TableHead>Result</TableHead>
              <ComparisonSubHeaderCells />
            </TableRow>
          </TableHeader>
          <TableBody>
            {pair.checkHistory.map((entry) => (
              <TableRow key={entry.checkId}>
                <TableCell className="text-muted-foreground">
                  <div>
                    {entry.feed === "at_estimate"
                      ? "Checked at estimate"
                      : "Rolling validation"}
                  </div>
                  {formatCheckRaisedBy(entry.raisedBy, entry.orderedAt) && (
                    <div className="text-xs text-muted-foreground">
                      {formatCheckRaisedBy(entry.raisedBy, entry.orderedAt)}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {formatRouteSourceLine(entry.routeSource)}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTimeOrDash(entry.checkedAt)}
                </TableCell>
                <TableCell>
                  <Badge variant={costCheckStateTone(entry.result)}>
                    {costCheckStateLabel(entry.result)}
                  </Badge>
                </TableCell>
                <ComparisonCells
                  value={{
                    kind: "money",
                    ours: entry.ourValuePence,
                    tharstern: entry.tharsternValuePence,
                    // The entry's own result is authoritative ("red" means
                    // systematic, not necessarily large) — never let a tiny
                    // delta paint a different colour than the Result badge.
                    toneOverride: costCheckStateTone(entry.result),
                  }}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function BreadcrumbsOnly({
  dictionaryItemId,
  children,
}: {
  dictionaryItemId: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs(dictionaryItemId)} />
      {children}
    </div>
  );
}

// ── A supplier route's cost ─────────────────────────────────────────────────
// What was sent (the request resolved from the item's mappings), what came
// back (net, VAT, gross, promised date, order-by cutoff, turnaround tier)
// and when, with every fetch attempt. An expired quote is shown only as
// history, labelled as not used — never as a cost (brief §6).

function SupplierRouteCostPairView({ routeCode }: { routeCode: string }) {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const {
    dictionaryItemId,
    variantId,
    quantity: quantityParam,
  } = useParams<{
    dictionaryItemId: string;
    variantId: string;
    quantity: string;
  }>();
  const quantity = quantityParam ? Number(quantityParam) : NaN;
  const [pair, setPair] = useState<SupplierRouteCostPair | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (!dictionaryItemId || !variantId || Number.isNaN(quantity)) return;
    setLoadError(false);
    adapter
      .getSupplierRouteCostPair(
        dictionaryItemId,
        variantId,
        quantity,
        routeCode,
      )
      .then((p) => {
        if (p === null) setNotFound(true);
        else setPair(p);
      })
      .catch(() => setLoadError(true));
  }, [adapter, dictionaryItemId, variantId, quantity, routeCode]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-destructive">
          Couldn&rsquo;t load this supplier cost.{" "}
          <button
            type="button"
            onClick={load}
            className="font-medium underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      </BreadcrumbsOnly>
    );
  }
  if (!dictionaryItemId || !variantId || Number.isNaN(quantity)) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">
          This cost pair link is malformed.
        </p>
      </BreadcrumbsOnly>
    );
  }
  if (notFound) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">
          No supplier cost here: this item, variant or quantity is not mapped to
          this fulfilment route. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }
  if (!pair) {
    return (
      <BreadcrumbsOnly dictionaryItemId={dictionaryItemId}>
        <p className="text-muted-foreground">Loading…</p>
      </BreadcrumbsOnly>
    );
  }

  const freshness = SUPPLIER_COST_FRESHNESS[pair.freshness];
  const isCurrent = pair.freshness === "live" || pair.freshness === "expiring";
  const supplierPence = isCurrent && pair.quote ? pair.quote.netPence : null;
  // A supplier-only item (#71) has no in-house side to compare against —
  // a state the backend serves, distinct from "not costed yet".
  const isMadeInHouse = pair.inHouse.status !== "no_in_house_route";
  const inHouse = describeInHouseCell(pair.inHouse, false, currentTime());

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={crumbs(
          dictionaryItemId,
          pair.dictionaryItemName,
          `${pair.variantLabel} × ${quantity.toLocaleString()} · ${pair.route.label}`,
        )}
      />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {pair.variantLabel}
        </h1>
        <p className="text-sm text-muted-foreground">
          {pair.dictionaryItemName} · {pair.route.label} cost
          {pair.isDeliveryIncluded ? " · delivery included" : ""}
        </p>
        <p className="text-sm">
          {isMadeInHouse ? (
            <Link
              to={paths.dictionaryCostPair({
                dictionaryItemId,
                variantId,
                quantity,
              })}
              className="font-medium underline underline-offset-2"
            >
              In-house cost check for this variant and quantity
            </Link>
          ) : (
            <span className="text-muted-foreground">Not made in-house</span>
          )}
        </p>
      </div>

      <div className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <Badge variant={freshness.tone} className="text-sm">
              {freshness.label}
            </Badge>
            <SummaryFigure
              label={`${pair.route.label}${pair.isDeliveryIncluded ? " (delivery included)" : ""}`}
              pence={supplierPence}
              emptyText={isCurrent ? "—" : "No current cost"}
            />
            {isMadeInHouse && (
              <>
                <SummaryFigure
                  label={IN_HOUSE_ROW_LABEL}
                  pence={inHouseFigurePence(pair.inHouse)}
                  emptyText="—"
                />
                <SummaryFigure
                  label={formatDifferenceLabel(pair.route.label)}
                  pence={pair.differencePence}
                  emptyText="—"
                  isSigned
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-muted-foreground">Quantity</span>
            <Select
              value={String(quantity)}
              onValueChange={(v) =>
                navigate(
                  paths.dictionaryCostPair({
                    dictionaryItemId,
                    variantId,
                    quantity: Number(v),
                    routeCode,
                  }),
                )
              }
            >
              <SelectTrigger aria-label="Quantity" className="h-7 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pair.quantityLadder.map((q) => (
                  <SelectItem key={q} value={String(q)}>
                    {q.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {pair.reason && (
          <p className="text-sm">
            {pair.freshness === "unmapped" ||
            pair.freshness === "errored" ||
            pair.freshness === "not_yet_quoted"
              ? `${freshness.label}: ${pair.reason}`
              : pair.reason}
          </p>
        )}
        {pair.listNotice && (
          <p className="text-sm">
            <Badge variant="warning">{pair.listNotice}</Badge>
          </p>
        )}
        {/* The in-house figure is the matrix's in-house cell, verdict and
            all — the same one the grid shows. */}
        {isMadeInHouse && (
          <p className="text-xs text-muted-foreground">
            {IN_HOUSE_ROW_LABEL}:{" "}
            <MarkMessage mark={inHouse.mark} message={inHouse.message} />
          </p>
        )}
        {isMadeInHouse && pair.isDeliveryIncluded && (
          <p className="text-xs text-muted-foreground">
            {DELIVERY_INCLUDED_NOTE}
          </p>
        )}
      </div>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">What we sent</h2>
        {pair.request === null ? (
          <p className="text-sm text-muted-foreground">
            Nothing has been sent to {pair.route.label} for this variant and
            quantity.{" "}
            <Link
              to={paths.dictionaryItemMappings({ dictionaryItemId })}
              className="font-medium text-foreground underline underline-offset-2"
            >
              Review this item&rsquo;s mappings
            </Link>
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{pair.route.label} attribute</TableHead>
                  <TableHead>Value sent</TableHead>
                  <TableHead>From our dictionary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pair.request.attributes.map((a) => (
                  <TableRow key={a.attribute}>
                    <TableCell className="font-mono text-xs">
                      {a.attribute}
                    </TableCell>
                    <TableCell>{a.value}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatRequestAttributeSource(a)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                Request as sent · fingerprint {pair.request.payloadHash}
              </summary>
              <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-3">
                {JSON.stringify(pair.request.body, null, 2)}
              </pre>
            </details>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          {pair.checkedAgainst ? (
            <>
              Checked against {pair.route.label}&rsquo;s product list refreshed{" "}
              {formatDateTimeOrDash(pair.checkedAgainst.refreshedAt)} (snapshot{" "}
              {pair.checkedAgainst.snapshotId}).
            </>
          ) : (
            <>
              {pair.route.label}&rsquo;s product list has not been checked for
              this variant yet.
            </>
          )}
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">
          {pair.quote && !isCurrent
            ? `What came back last (${freshness.label.toLowerCase()} — not used as a cost)`
            : "What came back"}
        </h2>
        {pair.quote === null ? (
          <p className="text-sm text-muted-foreground">
            Nothing has come back yet.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <QuoteFact
              label="Net — our trade-tier buy cost"
              value={formatCurrencyGbp(pair.quote.netPence / 100, 2)}
            />
            <QuoteFact
              label="VAT"
              value={formatPenceOrDash(pair.quote.vatPence)}
            />
            <QuoteFact
              label="Gross (not used)"
              value={formatPenceOrDash(pair.quote.grossPence)}
            />
            <QuoteFact
              label="Promised delivery"
              value={formatLongWeekdayDateOrDash(pair.quote.promisedDate)}
            />
            <QuoteFact
              label="Order by, to hit that date"
              value={formatDateTimeOrDash(pair.quote.orderBy)}
            />
            <QuoteFact
              label="Turnaround"
              value={pair.quote.turnaroundTier ?? "—"}
            />
          </dl>
        )}
        {pair.fetchedAt && (
          <p className="text-xs text-muted-foreground">
            Fetched {formatDateTimeOrDash(pair.fetchedAt)} by the nightly quote
            fetch
            {pair.expiresAt &&
              ` · ${isCurrent ? "valid until" : "expired"} ${formatDateTimeOrDash(pair.expiresAt)}`}
            .
          </p>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Fetch history</h2>
        {pair.attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No fetch has been attempted for this variant and quantity.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pair.attempts.map((attempt, index) => (
                <TableRow key={`${attempt.attemptedAt}:${index}`}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTimeOrDash(attempt.attemptedAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ATTEMPT_OUTCOME[attempt.outcome].tone}>
                      {ATTEMPT_OUTCOME[attempt.outcome].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {attempt.detail ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}

function formatPenceOrDash(pence: number | null): string {
  return pence == null ? "—" : formatCurrencyGbp(pence / 100, 2);
}

function SummaryFigure({
  label,
  pence,
  emptyText,
  isSigned = false,
}: {
  label: string;
  pence: number | null;
  emptyText: string;
  isSigned?: boolean;
}) {
  const text =
    pence == null
      ? emptyText
      : `${isSigned && pence > 0 ? "+" : ""}${formatCurrencyGbp(pence / 100, 2)}`;
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{text}</p>
    </div>
  );
}

function QuoteFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
